import { writable, type Writable, get, type Updater } from 'svelte/store'

/**
 * Helper function to read from localStorage if it's available.
 *
 * If not (SSR) then it will return undefined.
 */
export const readStorage = (name: string): string | null => {
  if (typeof window !== 'undefined') {
    return window.localStorage.getItem(name)
  }
  return null
}

/** Helper function to write to localStorage if it's available (not SSR). */
export const writeStorage = (name: string, value: string): void => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(name, value)
  }
}

/** Helper function to remove item from localStorage if it's available (not SSR). */
export const removeStorage = (name: string): void => {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(name)
  }
}

type SerializableSubType = boolean | string | number | null | undefined

type SerializableType =
  | SerializableSubType
  | { [key: string]: SerializableType }
  | SerializableType[]

/**
 * Returns a writable store that also persists the value in localStorage if
 * localStorage is available (browser).
 *
 * If `null` passed as value, the localStorage entry will also be removed.
 */
export const storable = <T extends SerializableType | null>(
  key: string,
  initialData: T,
): Writable<T> => {
  const storageValue = readStorage(key)
  const persisted = storageValue !== null ? JSON.parse(storageValue) : null

  const res = writable<T>(persisted ?? initialData)

  const set = (value: T) => {
    if (value === null) {
      removeStorage(key)
    } else {
      writeStorage(key, JSON.stringify(value))
    }
    res.set(value)
  }

  return {
    subscribe: res.subscribe,
    set,
    update: (fn: Updater<T>) => {
      const currentValue = get(res)
      const newValue = fn(currentValue)
      set(newValue)
    },
  }
}
